import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Assessment, Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import s3Utils from '../../src/utils/s3Utils';
import { decryptAppId, decryptText } from '../utils/encryption';
import previewReportService from '../services/previewReportService';


const MedicalPreviewReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [labDetailsState, setlabDetailsState] = useState([]);
  const [basicDetailsState, setBasicDetails] = useState([]);
  const [assessmentTypeid, setassessmentTypeid] = useState('');
  const [disciplineState, setDiscipline] = useState([]);
  const [locationDetailsState, setLocationDetails] = useState([]);
  const [scopeState, setScope] = useState([]);
  const [organisationDetailsState, setOrganisationDetails] = useState([]);
  const [equipmentDetailsState, setEquipmentDetails] = useState([]);
  const [referenceMaterialsState, setReferenceMaterials] = useState([]);
  const [observationsState, setObservations] = useState([]);
  const [nonAnalyticalInstrumentsState, setNonAnalyticalInstruments] = useState([]);
  const [form153PhantomsState, setForm153Phantoms] = useState([]);
  const [radioDetailsState, setRadioDetails] = useState([]);
  const [proficiencyTestingState, setProficiencyTesting] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [isObservationVisible, setIsObservationVisible] = useState(false);
  const [isAutoDownloading, setIsAutoDownloading] = useState(false);

  useEffect(() => {
    const encryptedAppId = getQueryParam("appId");
    const encryptedOrgId = getQueryParam("orgId");
    const encryptedObservation = getQueryParam("observation");

    console.log('🔍 PreviewReport - Encrypted parameters received:', {
      encryptedAppId,
      encryptedOrgId,
      encryptedObservation
    });

    if (encryptedAppId && encryptedOrgId && encryptedObservation) {
      try {
        // Decrypt the parameters
        const decryptedAppId = decryptAppId(encryptedAppId);
        const decryptedOrgId = decryptText(encryptedOrgId);
        const decryptedObservation = decryptText(encryptedObservation);

        console.log('🔓 PreviewReport - Decrypted parameters:', {
          appId: decryptedAppId,
          orgId: decryptedOrgId,
          observation: decryptedObservation
        });

        // Use decrypted parameters
        if (decryptedAppId && decryptedOrgId) {
          fetchForm153Data(decryptedAppId, decryptedOrgId);
        }
        setIsObservationVisible(decryptedObservation === "true");

        console.log('✅ PreviewReport - Parameters successfully decrypted and used');
      } catch (error) {
        console.error('❌ Error decrypting preview report parameters:', error);
        // Fallback to original behavior if decryption fails
        const appId = encryptedAppId;
        const orgId = encryptedOrgId;
        const observation = encryptedObservation;
        if (appId && orgId) {
          fetchForm153Data(appId, orgId);
        }
        setIsObservationVisible(observation === "true");
      }
    }
  }, [location.search]);

  // Handle auto-download functionality
  useEffect(() => {
    const autoDownload = getQueryParam("autoDownload");
    const fromSubmission = getQueryParam("fromSubmission");

    if (autoDownload === "true" && fromSubmission === "true" && !isAutoDownloading && labDetailsState.length > 0) {
      console.log('🚀 Auto-downloading PDF after form submission...');
      setIsAutoDownloading(true);

      // Small delay to ensure all data is loaded
      setTimeout(() => {
        handleAutoDownloadAndSave();
      }, 1000);
    }
  }, [labDetailsState, isAutoDownloading]);

  const getQueryParam = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  const fetchForm153Data = async (appId, orgId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/preview-report/${appId}/${orgId}`
      );

      if (response.data.success) {
        const {
          labDetails,
          basicDetails,
          discipline,
          locationDetails,
          scope,
          organisationDetails,
          equipmentDetails,
          referenceMaterials,
          observations,
          nonAnalyticalInstruments,
          form153Phantoms,
          form153KitsRadioisotopes,
          proficiencyTestingProviders
        } = response.data.data;

        setlabDetailsState(labDetails);
        setBasicDetails(basicDetails);
        setassessmentTypeid(basicDetails[0]?.AssessmentTypeId);
        setDiscipline(discipline);
        setLocationDetails(locationDetails);
        setScope(scope);
        setOrganisationDetails(organisationDetails);
        setEquipmentDetails(equipmentDetails);
        setReferenceMaterials(referenceMaterials);
        setObservations(observations);
        setNonAnalyticalInstruments(nonAnalyticalInstruments);
        setForm153Phantoms(form153Phantoms);
        setRadioDetails(form153KitsRadioisotopes);
        setProficiencyTesting(proficiencyTestingProviders);
        console.log('assessmentTypeId', basicDetails[0]?.AssessmentTypeId)
      } else {
        console.error("API Error:", response.data.message);
      }
    } catch (error) {
      console.error("Request Failed:", error.message);
    }
  };

  const getObservationBySection = (sectionName, sourceId) => {
    if (sourceId) {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase() &&
          item.sourceid === sourceId
      );
    } else {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase()
      );
    }
  };

  const getObservationBySubSection = (sectionName, subSectionName = "") => {
    return observationsState.find(
      (item) =>
        item.SectionName?.toLowerCase() === sectionName.toLowerCase() &&
        (subSectionName
          ? item.SubsectionName?.toLowerCase() === subSectionName.toLowerCase()
          : true)
    );
  };

  // Get observation text from the Observation field
  const getObservationText = (observationData) => {
    if (!observationData) return "";
    return observationData.Observation || "";
  };


  // Render observation cell content - displays the Observation text and status
  const renderObservationCell = (observationData) => {
    if (!observationData) return "No observation";

    const observation = observationData.Observation;
    const status = observationData.InformationStatus;
    const informationProvided = observationData.InformationProvided;

    // Check if observation text is "Document adequacy check completed" and status is Adequate
    const isAdequate = observation &&
      observation.trim().toLowerCase() === "document adequacy check completed" &&
      status && status.toLowerCase() === "adequate";

    // If status is Adequate, show green "Adequate"
    if (isAdequate) {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "green", fontWeight: "bold" }}>Adequate</span>
        </div>
      );
    }

    // If status is Inadequate, show InformationProvided in red
    if (status && status.toLowerCase() === "inadequate") {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "red", fontWeight: "bold" }}>
            {informationProvided || observation || "No information provided"}
          </span>
        </div>
      );
    }

    // Default: show observation in black
    return (
      <div style={{ textAlign: "left" }}>
        <span style={{ color: "black" }}>
          {observation || "No observation"}
        </span>
      </div>
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Auto-download PDF and save to database after form submission
  const handleAutoDownloadAndSave = async () => {
    console.log('🎯 Starting auto PDF generation and save process...');

    try {
      const encryptedappId = getQueryParam("appId");
      const encryptedorgId = getQueryParam("orgId");

      const appId = decryptAppId(encryptedappId);
      const orgId = decryptText(encryptedorgId);

      if (!appId || !orgId) {
        console.error('Missing appId or orgId for PDF generation');
        navigate('/main/dashboard');
        return;
      }

      console.log('📄 Auto-generating PDF...');

      // Generate PDF using the existing handleDownloadPDF logic but save to database
      const now = new Date();
      const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
      const timeStr = `${hour12}-${minutes}${ampm}`;
      const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

      // Create the same PDF as the download function but get it as blob
      const reportData = {
        labDetailsState,
        disciplineState,
        scopeState,
        organisationDetailsState,
        equipmentDetailsState,
        referenceMaterialsState,
        nonAnalyticalInstrumentsState,
        observationsState,
        form153PhantomsState,
        radioDetailsState,
        proficiencyTestingState
      };

      console.log('🔧 Generating PDF with comprehensive service...');

      // Use the preview report service to generate and save PDF
      const result = await previewReportService.generateAndSavePreviewReport(
        orgId,
        appId,
        3, // Medical lab type ID
        'PreviewReport',
        isObservationVisible
      );

      if (result.success) {
        console.log('✅ PDF generated and saved successfully!');
        console.log('📊 Result:', result);

        // Show success message and redirect to dashboard
        setTimeout(() => {
          console.log('🏠 Redirecting to dashboard...');
          navigate('/main/dashboard');
        }, 2000);
      } else {
        console.error('❌ Failed to generate and save PDF:', result.message);
        alert(`Failed to generate PDF: ${result.message}`);
        navigate('/main/dashboard');
      }

    } catch (error) {
      console.error('❌ Error in auto PDF generation and save:', error);
      alert(`Error generating PDF: ${error.message}`);
      navigate('/main/dashboard');
    }
  };

  // Download as PDF using jsPDF (Much more reliable for large documents)
  const handleDownloadPDF = () => {
    setDownloading(true);

    try {
      const appId = getQueryParam("appId");
      const now = new Date();
      const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
      const timeStr = `${hour12}-${minutes}${ampm}`;
      const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

      // Initialize jsPDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // Helper function to add page if needed
      const checkAndAddPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      // Add header with logo and title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('National Accreditation Board for Testing and', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 7;
      doc.text('Calibration Laboratories', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setFontSize(14);
      doc.text('Preview Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Lab Details Section
      const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Name of Laboratory: ${labDetail.CabName || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Contact Person: ${labDetail.NablContPerson || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Mobile: ${labDetail.ContactPersonMobileNo || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Email: ${labDetail.ContactPersonEmail1 || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`UserName: ${(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.LoginName : basicDetailsState?.LoginName) || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`CAB ID: ${(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.ApplicationNo : basicDetailsState?.ApplicationNo) || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Standard: ${(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.standard : basicDetailsState?.standard) || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Lab Type: ${(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.LabType : basicDetailsState?.LabType) || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Accreditation Category: ${(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.Assessmentype : basicDetailsState?.Assessmentype) || '-'}`, margin, yPosition);
      yPosition += 10;

      // Section: Lab General Details
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Lab General Details', margin + 2, yPosition + 5);
      yPosition += 12;

      // Helper function to get observations data for a section
      const getObservationsData = (sectionName) => {
        if (!isObservationVisible) return { text: '-', isAdequate: null };

        const relevantObservations = observationsState.filter(
          (item) => item.SectionName?.toLowerCase() === sectionName.toLowerCase()
        );

        if (relevantObservations.length === 0) return { text: '-', isAdequate: null };

        // Get only the first matching observation record to avoid duplicates
        const obsData = relevantObservations[0];

        // Get the observation text and status
        const observation = obsData.Observation || '';
        const status = obsData.InformationStatus;
        const informationProvided = obsData.InformationProvided;

        // Check if it's "Document adequacy check completed" and status is Adequate
        const isAdequate = observation.trim().toLowerCase() === "document adequacy check completed" &&
          status && status.toLowerCase() === "adequate";

        // If adequate, show "Adequate" in green
        if (isAdequate) {
          return { text: 'Adequate', isAdequate: true, status };
        }

        // If inadequate, show InformationProvided in red (or observation if no InformationProvided)
        if (status && status.toLowerCase() === "inadequate") {
          const text = informationProvided || observation || 'No information provided';
          return { text, isAdequate: false, status };
        }

        // Default: show observation
        return { text: observation || '-', isAdequate: null, status };
      };

      // Helper function to get observations text for backward compatibility
      const getObservationsText = (sectionName) => {
        return getObservationsData(sectionName).text;
      };

      // Helper function to build table row with conditional observation column
      const buildRow = (...values) => {
        return isObservationVisible ? [...values, ''] : values;
      };

      // Helper function to create table options with rowspan and color for observations
      const createTableWithObservations = (sectionName, bodyData) => {
        if (!isObservationVisible) {
          return {}; // Return empty object if observations are hidden
        }

        return {
          didParseCell: function (data) {
            // Only apply to body rows, not header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              data.cell.rowSpan = bodyData.length;
            } else if (data.section === 'body' && data.column.index === 2 && data.row.index > 0) {
              data.cell.styles.fillColor = false;
              data.cell.text = [];
            }
          },
          didDrawCell: function (data) {
            // Only draw custom text in body, not in header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              const obsData = getObservationsData(sectionName);
              if (obsData.isAdequate === true) {
                doc.setTextColor(0, 128, 0); // Green for adequate
              } else if (obsData.isAdequate === false) {
                doc.setTextColor(255, 0, 0); // Red for inadequate
              } else {
                doc.setTextColor(0, 0, 0); // Black for all other observations
              }

              const cellText = obsData.text;
              const lines = doc.splitTextToSize(cellText, data.cell.width - 4);
              doc.text(lines, data.cell.x + 2, data.cell.y + 4);
              doc.setTextColor(0, 0, 0); // Reset color
            }
          }
        };
      };

      // Lab Details Table - Complete with all fields
      const labDetailsData = [
        buildRow('Name of the CAB', labDetail.CabName || '-'),
        buildRow('Type of CAB', labDetail.CabType === 'Diagnostic' ? 'Medical Testing Laboratory' :
          labDetail.CabType === 'Medical Imaging' ? 'Medical Imaging' :
            labDetail.CabType === 'Both' ? 'Medical Testing Laboratory + Medical Imaging' : '-'),
        buildRow('GSTIN', labDetail.GSTIN || '-'),
        buildRow('GST Exemption Certificate', labDetail.GSTExemptionFile || '-'),
        buildRow('PAN/TAN No.', labDetail.PANTANNumber || '-'),
        buildRow('Address', labDetail.Address || '-'),
        buildRow('Country', labDetail.CountryName || '-'),
        buildRow('State/Province', labDetail.StateName || '-'),
        buildRow('City', labDetail.CityName || '-'),
        buildRow('District', labDetail.DistrictName || '-'),
        buildRow('Sub District', labDetail.SubDistrictName || '-'),
        buildRow('Village/Town', labDetail.VillageTown || '-'),
        buildRow('Pin Code', labDetail.Pincode || '-'),
        buildRow('Mobile No', labDetail.MobileNo || '-'),
        buildRow('Landline No', labDetail.LandlineNo || '-'),
        buildRow('Fax', labDetail.Fax || '-'),
        buildRow('Fax ISD Code', labDetail.FaxIsdCode || '-'),
        buildRow('Email', labDetail.Email || '-'),
        buildRow('Are you from SEZ region (Special economic zone)', labDetail.IsFromSEZ ? 'Yes' : 'No'),
      ];

      // Add SEZ Certificate if applicable
      if (labDetail.IsFromSEZ) {
        labDetailsData.push(buildRow('SEZ Certificate', labDetail.SEZCertificateFile || '-'));
      }

      // Add consultancy details
      labDetailsData.push(buildRow('Whether any individual or organization has provided consultancy for preparing towards NABL Accreditation', labDetail.HasConsultancy ? 'Yes' : 'No'));

      if (labDetail.HasConsultancy) {
        labDetailsData.push(buildRow('Name of Individual/Organization with city', labDetail.ConsultancyName || '-'));
        labDetailsData.push(buildRow('Registered Office Address', labDetail.ConsultancyAddress || '-'));
      }

      // Add adverse action details
      labDetailsData.push(buildRow('Has adverse action has been initiated/taken against the laboratory in the past?', labDetail.HasAdverseAction ? 'Yes' : 'No'));

      if (labDetail.HasAdverseAction === 'Yes') {
        labDetailsData.push(buildRow('Type of adverse action', labDetail.AdverseActionType || '-'));
        labDetailsData.push(buildRow('Adverse Decision Date', formatDate(labDetail.AdverseDecisionDate) || '-'));
        labDetailsData.push(buildRow('Previous Lab Id from NABL', labDetail.PreviousLabId || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: labDetailsData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...(isObservationVisible ? {
          didParseCell: function (data) {
            // Only apply to body rows, not header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              data.cell.rowSpan = labDetailsData.length;
            } else if (data.section === 'body' && data.column.index === 2 && data.row.index > 0) {
              // Hide cells that are part of rowspan
              data.cell.styles.fillColor = false;
              data.cell.text = [];
            }
          },
          didDrawCell: function (data) {
            // Only draw custom text in body, not in header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              const obsData = getObservationsData('Laboratory Details');
              if (obsData.isAdequate === true) {
                // Green for adequate
                doc.setTextColor(0, 128, 0);
              } else if (obsData.isAdequate === false) {
                // Red for inadequate
                doc.setTextColor(255, 0, 0);
              } else {
                // Black for all other observations
                doc.setTextColor(0, 0, 0);
              }

              // Draw text with color
              const cellText = obsData.text;
              const lines = doc.splitTextToSize(cellText, data.cell.width - 4);
              doc.text(lines, data.cell.x + 2, data.cell.y + 4);

              // Reset color
              doc.setTextColor(0, 0, 0);
            }
          }
        } : {})
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Legal Identity Details Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Legal Identity Details', margin, yPosition);
      yPosition += 8;

      const legalIdentityData = [
        buildRow('Legal Identity Type', labDetail.LegalIdentityType || '-'),
      ];

      // Add data based on legal identity type
      if (labDetail.LegalIdentityType === 'Proprietorship') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Name of the Owner', labDetail.OwnerName || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Registration Number', labDetail.MCIRegNumber || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Certificate', labDetail.LegalIdentityDoc1 || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate Number', labDetail.StateRegistrationNumber || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate', labDetail.LegalIdentityDoc2 || '-'));
      } else if (labDetail.LegalIdentityType === 'Partnership') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Partnership Deed', labDetail.CompaniesActCertificate || '-'));
        legalIdentityData.push(buildRow('Legal Identity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Name of the Owner', labDetail.OwnerName || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Registration Number', labDetail.MCIRegNumber || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Certificate', labDetail.LegalIdentityDoc1 || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate Number', labDetail.StateRegistrationNumber || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate', labDetail.LegalIdentityDoc2 || '-'));
      } else if (labDetail.LegalIdentityType === 'LLP') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Companies Act Certificate', labDetail.LLPCertificateFile || '-'));
      } else if (labDetail.LegalIdentityType === 'Companies Act') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Companies Act Certificate', labDetail.CompaniesActCertificateFile || '-'));
        legalIdentityData.push(buildRow('Address', labDetail.LegalIdentity_Address || '-'));
        legalIdentityData.push(buildRow('State', labDetail.LegalIdentity_State || '-'));
        legalIdentityData.push(buildRow('Pin Code', labDetail.CompaniesActPincode || '-'));
      } else if (labDetail.LegalIdentityType === 'Public Ltd') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Public Ltd Certificate', labDetail.PublicLtdCertificateFile || '-'));
        legalIdentityData.push(buildRow('Registered Office Address', labDetail.LegalIdentity_Address || '-'));
      } else if (labDetail.LegalIdentityType === 'Government') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Are you an Autonomous Body?', labDetail.GovernmentIsAutonomous ? 'Yes' : 'No'));
        legalIdentityData.push(buildRow('Gazette or Government Notification or self-Declaration on letter head by Head of the organization', labDetail.GovernmentCertificateFile || '-'));
      } else if (labDetail.LegalIdentityType === 'Trust') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Trust Certificate/Deed', labDetail.TrustCertificateFile || '-'));
      } else if (labDetail.LegalIdentityType === 'Society') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Society Certificate', labDetail.SocietyCertificateFile || '-'));
        legalIdentityData.push(buildRow('Are you an Autonomous Body?', labDetail.SocietyIsAutonomous ? 'Yes' : 'No'));
      } else if (labDetail.LegalIdentityType === 'One Person Company') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Registered Office Address', labDetail.LegalIdentity_Address || '-'));
        legalIdentityData.push(buildRow('One Person Company Certificate', labDetail.OPCCertificateFile || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: legalIdentityData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Legal Entity Type', legalIdentityData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Type Of Functioning Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Type Of Functioning', margin, yPosition);
      yPosition += 8;

      const functioningData = [
        buildRow('Type Of Functioning', labDetail.TypeOfFunctioning || '-'),
      ];

      if (labDetail.TypeOfFunctioning === 'hospital-owned') {
        functioningData.push(buildRow('Name Of Hospital', labDetail.HospitalName || '-'));
        functioningData.push(buildRow('Upload Hospital Registration Certificate', labDetail.HospitalRegistrationCertificateFile || '-'));
        functioningData.push(buildRow('Type of Service Provider', labDetail.HospitalServiceProvider || '-'));
        functioningData.push(buildRow('Address of Hospital', labDetail.LegalIdentity_Address || '-'));
        functioningData.push(buildRow('State of Hospital', labDetail.LegalIdentity_State || '-'));
        functioningData.push(buildRow('Pincode of Hospital', labDetail.HospitalPincode || '-'));
      } else if (labDetail.TypeOfFunctioning === 'hospital-outsource') {
        functioningData.push(buildRow('Name Of Outsource Laboratory', labDetail.OutsourceLaboratoryName || '-'));
        functioningData.push(buildRow('Hospital and Laboratory MOU', labDetail.HospitalLabMouFile || '-'));
        functioningData.push(buildRow('Laboratory Address of Outsource Laboratory', labDetail.OutsourceLabAddress || '-'));
        functioningData.push(buildRow('State of Outsource Laboratory', labDetail.OutsourceLabState || '-'));
        functioningData.push(buildRow('Pincode of Outsource Laboratory', labDetail.OutsourceLabPincode || '-'));
        functioningData.push(buildRow('Type of Service Provider of Outsource Laboratory', labDetail.OutsourceServiceProvider || '-'));
      } else if (labDetail.TypeOfFunctioning === 'cro-lab') {
        functioningData.push(buildRow('Type of Service Provider', labDetail.CroServiceProvider || '-'));
      } else if (labDetail.TypeOfFunctioning === 'others') {
        functioningData.push(buildRow('Type of Service Provider', labDetail.OthersServiceProvider || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: functioningData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Type Of Functioning', functioningData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Contact Person for NABL Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Contact Person for NABL', margin, yPosition);
      yPosition += 8;

      const contactPersonData = [
        buildRow('Contact Person for NABL', labDetail.NablContPerson || '-'),
        buildRow('Designation', labDetail.SpocDesig || '-'),
        buildRow('Mobile No', labDetail.ContactPersonMobileNo || '-'),
        buildRow('Landline No', labDetail.ContactPersonTelephoneNo || '-'),
        buildRow('Fax', labDetail.ContactPersonFaxNo || '-'),
        buildRow('Email 1', labDetail.ContactPersonEmail1 || '-'),
        buildRow('Email 2', labDetail.ContactPersonEmail2 || '-'),
      ];

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: contactPersonData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Contact Person from laboratory for NABL', contactPersonData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Category of Facility Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Category of Facility(s) Applied Under the Scope of Accreditation', margin, yPosition);
      yPosition += 8;

      const categoryData = [
        buildRow('Permanant Facility', labDetail.PermanentFacility ? 'Yes' : 'No'),
        buildRow('Site Facility', labDetail.SiteFacility ? 'Yes' : 'No'),
        buildRow('POCT (Point of Care Testing)', labDetail.POCTFacility ? 'Yes' : 'No'),
        ...(labDetail.CabType !== 'Medical Imaging'
          ? [buildRow('Category of which accreditation is being sought', labDetail.AccreditationCategory || '-')]
          : [])
      ];


      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: categoryData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Category of Facility(s) Applied Under the Scope of Accreditation', categoryData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Parent Organisation Details Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Parent Organisation Details', margin, yPosition);
      yPosition += 8;

      const parentOrgData = [
        buildRow('Name of the Parent Organization Applicable?', labDetail.IsParentOrgApplicable ? 'Yes' : 'No'),
      ];

      if (labDetail.IsParentOrgApplicable) {
        parentOrgData.push(buildRow('Name of the Parent Organization', labDetail.ParentOrgName || '-'));
        parentOrgData.push(buildRow('Parent Organization Contact No.', labDetail.ParentOrgContactNo || '-'));
        parentOrgData.push(buildRow('Parent Organization Landline No.', labDetail.ParentOrgLandlineNo || '-'));
        parentOrgData.push(buildRow('Parent Organization Fax No.', labDetail.ParentOrgFaxNo || '-'));
        parentOrgData.push(buildRow('Parent Organization Email Id', labDetail.ParentOrgEmailId || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: parentOrgData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Parent Organisation Details', parentOrgData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Collection of Sample From Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Collection of Sample From', margin, yPosition);
      yPosition += 8;

      const sampleCollectionData = [
        buildRow('Sample Collection Centre(s)/ Facility(ies)', labDetail.SampleColletion ? 'Yes' : 'No'),
      ];

      if (labDetail.SampleColletion) {
        sampleCollectionData.push(buildRow('No of Collection Centres', labDetail.NumberOfCollectionCenters || '-'));
        sampleCollectionData.push(buildRow('Relevant Document', labDetail.SampleCollectionDocument || '-'));
      }

      sampleCollectionData.push(buildRow('Home collection', labDetail.HomeColletion ? 'Yes' : 'No'));

      if (labDetail.HomeColletion) {
        sampleCollectionData.push(buildRow('Relevant Document', labDetail.HomeCollectionDocument || '-'));
      }

      sampleCollectionData.push(buildRow('Other Source(s)', labDetail.OtherSource ? 'Yes' : 'No'));

      if (labDetail.OtherSource) {
        sampleCollectionData.push(buildRow('Relevant Document', labDetail.OtherSourcesDocument || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: sampleCollectionData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Collection of Sample From', sampleCollectionData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Internal Audit and Management Review Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Internal Audit and Management Review', margin, yPosition);
      yPosition += 8;

      const auditData = [
        buildRow('Start Date of Last Internal Audit', formatDate(labDetail.InternalAuditFromDate) || '-'),
        buildRow('End Date of Last Internal Audit', formatDate(labDetail.InternalAuditToDate) || '-'),
        buildRow('Internal Audit Document', labDetail.InternalAuditDocument || '-'),
        buildRow('Whether all requirements of ISO 15189:2022 covering all activities of laboratory have been audited at least once in last one year?', labDetail.ISO15189Audited ? 'Yes' : 'No'),
        buildRow('Whether various locations (including collection centers) covered in the audit?', labDetail.LocationsAudited ? 'Yes' : 'No'),
        buildRow('Whether Pre and post examination activities included in the audit schedule?', labDetail.PrePostExamAudited ? 'Yes' : 'No'),
        buildRow('Start Date of Last Management Review', formatDate(labDetail.ManagementReviewFromDate) || '-'),
        buildRow('End Date of Last Management Review', formatDate(labDetail.ManagementReviewToDate) || '-'),
        buildRow('Management Review Document', labDetail.ManagementReviewDocument || '-'),
      ];

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: auditData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 70 }
        } : {
          0: { cellWidth: 70 },
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Internal Audit and Management Review', auditData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Disciplines Section
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Disciplines', margin + 2, yPosition + 5);
      yPosition += 12;

      if (disciplineState.length > 0) {
        const disciplineData = disciplineState.map((discipline, index) => [
          index + 1,
          discipline.DisciplineName || '-',
          '-'
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Sr No.', 'Discipline Name']],
          body: disciplineData,
          theme: 'grid',
          headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
          styles: { fontSize: 7, cellPadding: 2 },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }



      // Equipment Details Section - Horizontal format
      if (equipmentDetailsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Equipment Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Create horizontal table for equipment details
        const equipmentTableData = equipmentDetailsState.map((equipment, index) => {
          const equipmentInfo = `Equipment Name: ${equipment.NameOfEquipment || '-'}\nEquipment ID: ${equipment.Equipmentid || equipment.EquipmentId || '-'}\nUID: ${equipment.UID || equipment.Uid || '-'}\nModel: ${equipment.Model || '-'}\nSerial No: ${equipment.SerialNumber || equipment.SerialNo || '-'}`;

          const technicalInfo = `Manufacturer: ${equipment.Make || equipment.MakeCompany || equipment.ManufacturerName || '-'}\nYear of Make: ${equipment.YearOfMake || equipment.ManufacturingYear || '-'}\nReceipt Date: ${equipment.ReceiptDate ? formatDate(equipment.ReceiptDate) : (equipment.DatePlacedInService ? formatDate(equipment.DatePlacedInService) : '-')}\nInstallation Date: ${equipment.InstallationDate ? formatDate(equipment.InstallationDate) : (equipment.DateOfInstallation ? formatDate(equipment.DateOfInstallation) : '-')}\nLimit of Detection: ${equipment.LimitOfDetection || equipment.LOD || equipment.RangeCapacity || '-'}`;

          const calibrationInfo = `Calibration Date: ${equipment.CalibrationDate ? formatDate(equipment.CalibrationDate) : (equipment.LastCalibrationDate ? formatDate(equipment.LastCalibrationDate) : (equipment.DateOfLastCalibration ? formatDate(equipment.DateOfLastCalibration) : '-'))}\nCalibration Due: ${equipment.CalibrationDueDate ? formatDate(equipment.CalibrationDueDate) : (equipment.NextCalibrationDate ? formatDate(equipment.NextCalibrationDate) : (equipment.CalibrationDueOn ? formatDate(equipment.CalibrationDueOn) : '-'))}\nCalibrated By: ${equipment.CalibratedBy || equipment.CalibrationType || equipment.CalibrationBy || equipment.CalibratedByType || '-'}\nCalibration Agency: ${equipment.CalibrationAgency || equipment.ExternalLaboratory || equipment.CalibrationAgencyName || equipment.ExternalCalibrationAgency || equipment.InHouseCalibration || '-'}\nTraceability: ${equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || '-'}`;

          if (isObservationVisible) {
            const observation = getObservationBySection("Equipment", `${equipment.Id}`);
            const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
            return [
              index + 1,
              equipment.DisciplineName || '-',
              equipmentInfo,
              technicalInfo,
              calibrationInfo,
              observationText
            ];
          } else {
            return [
              index + 1,
              equipment.DisciplineName || '-',
              equipmentInfo,
              technicalInfo,
              calibrationInfo
            ];
          }
        });

        const equipmentTableHeaders = isObservationVisible
          ? ['Sr No.', 'Discipline', 'Equipment Information', 'Technical Specifications', 'Calibration Information', 'Observations']
          : ['Sr No.', 'Discipline', 'Equipment Information', 'Technical Specifications', 'Calibration Information'];

        autoTable(doc, {
          startY: yPosition,
          head: [equipmentTableHeaders],
          body: equipmentTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [219, 216, 216],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
            halign: 'center'
          },
          styles: {
            fontSize: 6,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top'
          },
          columnStyles: isObservationVisible ? {
            0: { cellWidth: 15 }, // Sr No
            1: { cellWidth: 25 },
            2: { cellWidth: 40 },
            3: { cellWidth: 40 },
            4: { cellWidth: 40 },
            5: { cellWidth: 25 }
          } : {
            0: { cellWidth: 15 }, // Sr No
            1: { cellWidth: 30 },
            2: { cellWidth: 50 },
            3: { cellWidth: 50 },
            4: { cellWidth: 45 }
          },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // Reference Materials Section - Horizontal format
      if (referenceMaterialsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Reference Material Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Create horizontal table for reference materials
        const refMaterialTableData = referenceMaterialsState.map(material => {
          const materialInfo = `Name: ${material.Name || '-'}\nType: Reference Material/Strain/Culture\nDate of Expiry: ${(material.DateOfExpiry && formatDate(material.DateOfExpiry) !== "01/01/1900" ? formatDate(material.DateOfExpiry) : '-')}`;

          const sourceTraceability = `Source: ${material.Source || '-'}\nTraceability: ${material.Traceability || '-'}`;

          if (isObservationVisible) {
            const observation = getObservationBySection("Reference Material", material.Id);
            const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
            return [
              material.DisciplineName || '-',
              materialInfo,
              sourceTraceability,
              observationText
            ];
          } else {
            return [
              material.DisciplineName || '-',
              materialInfo,
              sourceTraceability
            ];
          }
        });

        const refMaterialHeaders = isObservationVisible
          ? ['Discipline', 'Material Information', 'Source & Traceability', 'Observations']
          : ['Discipline', 'Material Information', 'Source & Traceability'];

        autoTable(doc, {
          startY: yPosition,
          head: [refMaterialHeaders],
          body: refMaterialTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [219, 216, 216],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
            halign: 'center'
          },
          styles: {
            fontSize: 6,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top'
          },
          columnStyles: isObservationVisible ? {
            0: { cellWidth: 30, halign: 'left' },
            1: { cellWidth: 70, halign: 'left' },
            2: { cellWidth: 60, halign: 'left' },
            3: { cellWidth: 30, halign: 'left' }
          } : {
            0: { cellWidth: 30, halign: 'left' },
            1: { cellWidth: 80, halign: 'left' },
            2: { cellWidth: 80, halign: 'left' }
          },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // Scope Section - Horizontal format with merged columns
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Proposed Scope', margin + 2, yPosition + 5);
      yPosition += 12;

      if (scopeState.length > 0) {
        // Sort scope data by discipline name first
        const sortedScopeData = [...scopeState].sort((a, b) => {
          const disciplineA = (a.DisciplineName || '').toLowerCase();
          const disciplineB = (b.DisciplineName || '').toLowerCase();
          return disciplineA.localeCompare(disciplineB);
        });

        // Create horizontal table with merged columns
        const scopeTableData = sortedScopeData.map((scope, index) => {
          const srNo = scope.SrNo || (index + 1);
          const scopeDetails = `Test Code: ${scope.TestCode || '-'}\nMaterials/Products: ${scope.GroupTested || '-'}\nSpecific Test: ${scope.TypeOfTest || '-'}\nTest Method: ${scope.ScopeStandard || '-'}`;

          const rangeDetails = `Type: ${scope.rangeType || '-'}\nFacility: ${scope.testPerformedAt || '-'}\nRange: ${scope.RangeLL || '-'} ${scope.RangeLLUnit || '-'} To ${scope.RangeUL || '-'} ${scope.RangeULUnit || '-'}\n%CV/MU: ${scope.cv ? `CV: ${scope.cv}` : ''}${scope.mu ? ` MU: ${scope.mu}` : ''}${!scope.cv && !scope.mu ? '-' : ''}${scope.rangeRemark ? `\nRemarks: ${scope.rangeRemark}` : ''}`;

          const equipmentDetails = `Equipment: ${scope.Equipment || '-'}\nReference Material: ${scope.RefName || '-'}\nProficiency Testing: ${scope.PtName || '-'}`;

          if (isObservationVisible) {
            const observation = getObservationBySection("Scope", scope.Id);
            const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
            return [
              srNo,
              scope.DisciplineName || '-',
              scopeDetails,
              rangeDetails,
              equipmentDetails,
              observationText
            ];
          } else {
            return [
              srNo,
              scope.DisciplineName || '-',
              scopeDetails,
              rangeDetails,
              equipmentDetails
            ];
          }
        });

        const tableHeaders = isObservationVisible
          ? ['Sr No.', 'Discipline', 'Scope Details', 'Range Details', 'Equipment Details', 'Observations']
          : ['Sr No.', 'Discipline', 'Scope Details', 'Range Details', 'Equipment Details'];

        autoTable(doc, {
          startY: yPosition,
          head: [tableHeaders],
          body: scopeTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [219, 216, 216],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
            halign: 'center'
          },
          styles: {
            fontSize: 6,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top'
          },
          columnStyles: isObservationVisible ? {
            0: { cellWidth: 12 }, // Sr No
            1: { cellWidth: 25 },
            2: { cellWidth: 40 },
            3: { cellWidth: 40 },
            4: { cellWidth: 40 },
            5: { cellWidth: 25 }
          } : {
            0: { cellWidth: 12 }, // Sr No
            1: { cellWidth: 30 },
            2: { cellWidth: 50 },
            3: { cellWidth: 50 },
            4: { cellWidth: 45 }
          },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // Proficiency Testing Section - Horizontal format
      if (proficiencyTestingState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Proficiency Testing Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Create horizontal table for proficiency testing - matching the UI table structure
        const ptTableData = proficiencyTestingState.length > 0 ?
          proficiencyTestingState.map((pt) => {
            const ptDetails = `Participation In: ${pt.participationIn || '-'}\nNodal Laboratory: ${pt.nodalLaboratory || '-'}\nOther Details: ${pt.nodalLaboratoryOthers || '-'}`;

            const ilcDetails = `ILC Details: ${pt.ilcDetails || '-'}`;

            const remarksDetails = `Remarks: ${pt.remarks || '-'}\nCreated: ${pt.CreatedOn ? new Date(pt.CreatedOn).toLocaleDateString() : '-'}`;

            if (isObservationVisible) {
              const observation = getObservationBySection("Proficiency Testing", `${pt.Id}`);
              console.log("Observation for Proficiency Testing ID", pt.Id, observation);
              const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
              return [
                ptDetails,
                ilcDetails,
                remarksDetails,
                observationText
              ];
            } else {
              return [
                ptDetails,
                ilcDetails,
                remarksDetails
              ];
            }
          }) :
          [isObservationVisible ? ['No proficiency testing details specified', '', '', ''] : ['No proficiency testing details specified', '', '']];

        const ptHeaders = isObservationVisible
          ? ['PT Details', 'ILC Details', 'Remarks', 'Observations']
          : ['PT Details', 'ILC Details', 'Remarks'];

        autoTable(doc, {
          startY: yPosition,
          head: [ptHeaders],
          body: ptTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [219, 216, 216],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
            halign: 'center'
          },
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top'
          },
          columnStyles: isObservationVisible ? {
            0: { cellWidth: 50, halign: 'left' },
            1: { cellWidth: 40, halign: 'left' },
            2: { cellWidth: 40, halign: 'left' },
            3: { cellWidth: 40, halign: 'left' }
          } : {
            0: { cellWidth: 60, halign: 'left' },
            1: { cellWidth: 55, halign: 'left' },
            2: { cellWidth: 65, halign: 'left' }
          },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      if (organisationDetailsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Organisation Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Create horizontal table for organization details
        const organizationTableData = organisationDetailsState.map(org => {
          const personalInfo = `Name: ${org.Name || '-'}\nDesignation: ${org.Designation || '-'}\nQualification: ${org.Qualification || '-'}\nSpecialization: ${org.Specialization || '-'}\nExperience: ${org.RelevantExperience || org.Experience || '-'} years\nTraining: ${org.RelevantTraining || '-'}`;

          const contactInfo = `Landline: ${org.LandlineNo || '-'}\nMobile: ${org.ContactNo || '-'}\nEmail: ${org.EmailID || '-'}`;

          const workInfo = `Employee Type: ${org.EmployeeType || '-'}\nEngagement Type: ${org.typeofEngagement || '-'}\nTiming: ${org.Timming || '-'}\nAuthorized Signatory: ${org.IsAuthorisedSignatory ? 'Yes' : 'No'}${org.IsAuthorisedSignatory && org.SignatorySign ? '\nSignatory File: Available' : ''}`;

          if (isObservationVisible) {
            const observation = getObservationBySection("Staff", org.Id);
            const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
            return [
              org.DisciplineName || '-',
              personalInfo,
              contactInfo,
              workInfo,
              observationText
            ];
          } else {
            return [
              org.DisciplineName || '-',
              personalInfo,
              contactInfo,
              workInfo
            ];
          }
        });

        const orgTableHeaders = isObservationVisible
          ? ['Discipline', 'Personal Information', 'Contact Information', 'Work Information', 'Observations']
          : ['Discipline', 'Personal Information', 'Contact Information', 'Work Information'];

        autoTable(doc, {
          startY: yPosition,
          head: [orgTableHeaders],
          body: organizationTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [219, 216, 216],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
            halign: 'center'
          },
          styles: {
            fontSize: 6,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top'
          },
          columnStyles: isObservationVisible ? {
            0: { cellWidth: 25, halign: 'left' },
            1: { cellWidth: 50, halign: 'left' },
            2: { cellWidth: 40, halign: 'left' },
            3: { cellWidth: 45, halign: 'left' },
            4: { cellWidth: 30, halign: 'left' }
          } : {
            0: { cellWidth: 30, halign: 'left' },
            1: { cellWidth: 60, halign: 'left' },
            2: { cellWidth: 50, halign: 'left' },
            3: { cellWidth: 50, halign: 'left' }
          },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }


      // Organization Details Section




      // Non Analytical Instruments Section - Horizontal format
      if (nonAnalyticalInstrumentsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Non Analytical Instruments Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Create horizontal table for non-analytical instruments (similar to scope format)
        const instrumentTableData = nonAnalyticalInstrumentsState.map(instrument => {
          const instrumentDetails = `Instrument Name: ${instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : '-'}\nUID: ${instrument.UID || '-'}\nType: ${instrument.InstrumentType || '-'}\nModel: ${instrument.Model || '-'}\nMake: ${instrument.Make || '-'}`;

          const calibrationDetails = `Calibration Frequency: ${instrument.Calibration_Frequency || '-'}\nLast Calibration Date: ${formatDate(instrument.LastCalibration_Date) || '-'}\nNext Calibration Date: ${formatDate(instrument.NextCalibration_Date) || '-'}\nCalibration Agency: ${instrument.CalibrationAgency || '-'}`;

          const statusDetails = `Status: ${instrument.Status || 'Active'}\nLocation: ${instrument.Location || '-'}\nPurchase Date: ${formatDate(instrument.PurchaseDate) || '-'}\nCondition: ${instrument.Condition || '-'}`;

          if (isObservationVisible) {
            const observation = getObservationBySection("Non-Analytical Instruments", instrument.id);
            const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
            return [
              instrumentDetails,
              calibrationDetails,
              statusDetails,
              observationText
            ];
          } else {
            return [
              instrumentDetails,
              calibrationDetails,
              statusDetails
            ];
          }
        });

        const instrumentHeaders = isObservationVisible
          ? ['Instrument Details', 'Calibration Details', 'Status Details', 'Observations']
          : ['Instrument Details', 'Calibration Details', 'Status Details'];

        autoTable(doc, {
          startY: yPosition,
          head: [instrumentHeaders],
          body: instrumentTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [219, 216, 216],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
            halign: 'center'
          },
          styles: {
            fontSize: 6,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top'
          },
          columnStyles: isObservationVisible ? {
            0: { cellWidth: 45, halign: 'left' },
            1: { cellWidth: 45, halign: 'left' },
            2: { cellWidth: 45, halign: 'left' },
            3: { cellWidth: 35, halign: 'left' }
          } : {
            0: { cellWidth: 60, halign: 'left' },
            1: { cellWidth: 65, halign: 'left' },
            2: { cellWidth: 65, halign: 'left' }
          },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

      // Phantoms Section (for Medical Imaging labs)
      if ((labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && form153PhantomsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Phantoms Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process phantoms in chunks
        const chunkSize = 10;
        for (let i = 0; i < form153PhantomsState.length; i += chunkSize) {
          const chunk = form153PhantomsState.slice(i, i + chunkSize);

          chunk.forEach((phantom, idx) => {
            checkAndAddPage(40);

            // Phantom item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Phantom #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Phantom details table
            const phantomData = [
              buildRow('Phantom Type', phantom.PhantomType || '-'),
              buildRow('Discipline', phantom.DisciplineName || '-'),
              buildRow('Type of Test', phantom.TypeOfTestNames || '-'),
              buildRow('Specifications', phantom.Specifications || '-'),
              buildRow('Date of Placing', formatDate(phantom.DateofPlacing) || '-'),
              buildRow('Traceability', phantom.Traceability || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: phantomData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: {
                fontSize: 7,
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? {
                0: { cellWidth: 50, fontStyle: 'bold' },
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' },
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('phantoms', phantomData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }

      // Radioisotopes/Test Kits Section (for Medical Imaging labs)
      if ((labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && radioDetailsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Radioisotopes/Test Kits Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process radioisotopes in chunks
        const chunkSize = 10;
        for (let i = 0; i < radioDetailsState.length; i += chunkSize) {
          const chunk = radioDetailsState.slice(i, i + chunkSize);

          chunk.forEach((radio, idx) => {
            checkAndAddPage(45);

            // Radio item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Radioisotope/Test Kit #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Radio details table
            const radioData = [
              buildRow('Name of Radioisotopes', radio.NameofRadioisotopes || '-'),
              buildRow('Discipline', radio.DisciplineName || '-'),
              buildRow('Type of Test', radio.TypeOfTestNames || '-'),
              buildRow('Half Life', radio.HalfLife || '-'),
              buildRow('Energy (KeV)', radio.EnergyKeV || '-'),
              buildRow('Max Activity Permitted', radio.MaxactivityPermitted || '-'),
              buildRow('Details of Supplier', radio.DetailsofSupplier || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: radioData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: {
                fontSize: 7,
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? {
                0: { cellWidth: 50, fontStyle: 'bold' },
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' },
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('radioisotopes', radioData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }

      // Organization Chart Section - Matching UI table structure
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Organization Chart', margin + 2, yPosition + 5);
      yPosition += 12;

      // Create table data matching the UI structure exactly
      const orgChartData = [
        [
          'Organization Chart Document',
          labDetail.OrganizationChart ? 'View' : '-'
        ],
        [
          'Parent Organization Chart Document',
          labDetail.ParentOrganizationChart ? 'View' : '-'
        ],
        [
          'Management System Document',
          labDetail.ManagementSystemDocument ? 'View' : '-'
        ]
      ];

      const orgChartHeaders = isObservationVisible
        ? ['Document Type', 'Status', 'Observations']
        : ['Document Type', 'Status'];

      // Add observations column if needed
      if (isObservationVisible) {
        orgChartData.forEach((row, index) => {
          let observation;
          if (index === 0) {
            observation = getObservationBySubSection("Organization Details", "organization-details");
          } else if (index === 1) {
            observation = getObservationBySubSection("Organization Details", "parent-organization");
          } else {
            observation = getObservationBySubSection("Organization Details", "management-system");
          }
          const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
          row.push(observationText);
        });
      }

      autoTable(doc, {
        startY: yPosition,
        head: [orgChartHeaders],
        body: orgChartData,
        theme: 'grid',
        headStyles: {
          fillColor: [219, 216, 216],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center'
        },
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: 'linebreak',
          valign: 'top'
        },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 80, halign: 'right', fontStyle: 'bold' },
          1: { cellWidth: 60, halign: 'left' },
          2: { cellWidth: 50, halign: 'left' }
        } : {
          0: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
          1: { cellWidth: 90, halign: 'left' }
        },
        margin: { left: margin, right: margin },
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Proficiency Testing Documents Section - Matching UI table structure
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Proficiency Testing Documents', margin + 2, yPosition + 5);
      yPosition += 12;

      // Create table data matching the UI structure exactly
      const ptDocData = [
        [
          'Proficiency Testing Documents',
          labDetail.QMSFile ? 'View' : '-'
        ]
      ];

      const ptDocHeaders = isObservationVisible
        ? ['Document Type', 'Status', 'Observations']
        : ['Document Type', 'Status'];

      // Add observations column if needed
      if (isObservationVisible) {
        ptDocData.forEach(row => {
          const observation = getObservationBySubSection("Proficiency Testing Document", "enclosure");
          const observationText = observation ? (observation.ObservationRemarks || observation.Remarks || '-') : '-';
          row.push(observationText);
        });
      }

      autoTable(doc, {
        startY: yPosition,
        head: [ptDocHeaders],
        body: ptDocData,
        theme: 'grid',
        headStyles: {
          fillColor: [219, 216, 216],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center'
        },
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: 'linebreak',
          valign: 'top'
        },
        columnStyles: isObservationVisible ? {
          0: { cellWidth: 80, halign: 'right', fontStyle: 'bold' },
          1: { cellWidth: 60, halign: 'left' },
          2: { cellWidth: 50, halign: 'left' }
        } : {
          0: { cellWidth: 100, halign: 'right', fontStyle: 'bold' },
          1: { cellWidth: 90, halign: 'left' }
        },
        margin: { left: margin, right: margin },
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Declaration Section
      checkAndAddPage(50);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 10, 'F');
      doc.text('DECLARATION', pageWidth / 2, yPosition + 6, { align: 'center' });
      yPosition += 15;

      // Terms & Conditions Section
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Terms & Conditions', margin, yPosition);
      yPosition += 8;

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');

      const termsText = `The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.

The Conformity Assessment Body {Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:

By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.

Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.`;

      const termsLines = doc.splitTextToSize(termsText, contentWidth);
      doc.text(termsLines, margin, yPosition);
      yPosition += termsLines.length * 4 + 10;

      // Declaration by the Laboratory Section
      checkAndAddPage(40);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Declaration by the Laboratory', margin, yPosition);
      yPosition += 8;

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text('We declare that:', margin, yPosition);
      yPosition += 6;

      const declarationPoints = [
        'We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.',
        'We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).',
        'We have declared all sources of samples.',
        'We have declared and submitted accurate and complete information in the enclosed "Declaration Form"',
        'We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.',
        'We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).',
        'We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.',
        'We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.',
        'We comply to all national, regional and local regulatory requirements for operating a laboratory.',
        'All information provided in this application is true.',
        'Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO 15189.'
      ];

      declarationPoints.forEach((point, index) => {
        checkAndAddPage(15);
        const pointText = `${index + 1}. ${point}`;
        const pointLines = doc.splitTextToSize(pointText, contentWidth - 5);
        doc.text(pointLines, margin, yPosition);
        yPosition += pointLines.length * 4 + 3;
      });

      yPosition += 10;

      // Add additional declaration text
      checkAndAddPage(20);
      const additionalText1 = `${labDetail.DeclNameOrg || 'The authorized representative of CAB'}, authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.`;
      const additionalLines1 = doc.splitTextToSize(additionalText1, contentWidth);
      doc.text(additionalLines1, margin, yPosition);
      yPosition += additionalLines1.length * 4 + 8;

      checkAndAddPage(20);
      const additionalText2 = `I have read and understood all the terms and conditions mentioned above. *`;
      const additionalLines2 = doc.splitTextToSize(additionalText2, contentWidth);
      doc.text(additionalLines2, margin, yPosition);
      yPosition += additionalLines2.length * 4 + 8;

      checkAndAddPage(30);
      const additionalText3 = `By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'. *`;
      const additionalLines3 = doc.splitTextToSize(additionalText3, contentWidth);
      doc.text(additionalLines3, margin, yPosition);
      yPosition += additionalLines3.length * 4 + 10;

      // Add footer with page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          `Generated on: ${new Date().toLocaleString()}`,
          pageWidth - margin,
          pageHeight - 10,
          { align: 'right' }
        );
      }

      // Save the PDF
      doc.save(filename);

      console.log(`✅ PDF generated successfully with ${totalPages} pages`);
      alert(`PDF generated successfully with ${totalPages} pages!`);

    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Error generating PDF. Please check console for details.");
    } finally {
      setDownloading(false);
    }
  };

  // Get the first lab detail (assuming there's at least one)
  const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};


  const viewS3File = async (fileName) => {
    try {
      const url = await s3Utils.generateSignedUrlForFilename(fileName);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Error generating S3 URL:", error);
    }
  };

  return (
    Number(assessmentTypeid) === 11 ? (
      <Box sx={{ p: 3, mt: 7, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Organization Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
              minWidth: 1000
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Sr No.
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Discipline
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Personal Information
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Contact Information
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                  Work Information
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Observation
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {organisationDetailsState.length > 0 ? (
                organisationDetailsState.filter(staff => staff.CompetenceAssessmentFile).map((staff, idx) => (
                  <TableRow key={staff.Id}>
                    <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      {staff.DisciplineName || "-"}
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div><strong>Name:</strong> {staff.Name || "-"}</div>
                        <div><strong>Designation:</strong> {staff.Designation || "-"}</div>
                        <div><strong>Qualification:</strong> {staff.Qualification || "-"}</div>
                        <div><strong>Specialization:</strong> {staff.Specialization || "-"}</div>
                        <div><strong>Experience:</strong> {staff.RelevantExperience || "-"} years</div>
                        <div><strong>Training:</strong> {staff.RelevantTraining || "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div><strong>Landline:</strong> {staff.LandlineNo || "-"}</div>
                        <div><strong>Mobile:</strong> {staff.ContactNo || "-"}</div>
                        <div><strong>Email:</strong> {staff.EmailID || "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div><strong>Employee Type:</strong> {staff.EmployeeType || "-"}</div>
                        <div><strong>Engagement Type:</strong> {staff.typeofEngagement || "-"}</div>
                        <div><strong>Timing:</strong> {staff.Timming || "-"}</div>
                        <div><strong>Authorized Signatory:</strong> {staff.IsAuthorisedSignatory ? "Yes" : "No"}</div>
                        {staff.IsAuthorisedSignatory && staff.SignatorySign && (
                          <div><strong>Signatory File:</strong> <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(staff.SignatorySign);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.photo && (
                          <div><strong>Photo:</strong> <a
                            href={staff.photo}
                            target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.TrainingCertificateFile && (
                          <div><strong>Training Certificate File:</strong> <a
                            href={staff.TrainingCertificateFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.AppointmentLetterFile && (
                          <div><strong>Appointment Letter File:</strong> <a
                            href={staff.AppointmentLetterFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.BioDataFile && (
                          <div><strong>Bio Data File:</strong> <a
                            href={staff.BioDataFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.CompetenceAssessmentFile && (
                          <div><strong>Competence Assessment File:</strong> <a
                            href={staff.CompetenceAssessmentFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.ExperienceCertificateFile && (
                          <div><strong>Experience Certificate File:</strong> <a
                            href={staff.ExperienceCertificateFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                        {staff.QualificationCertificateFile && (
                          <div><strong>Qualification Certificate File:</strong> <a
                            href={staff.QualificationCertificateFile}
                           target="_blank"
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a></div>
                        )}
                      </div>
                    </TableCell>
                    {isObservationVisible && (
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {renderObservationCell(getObservationBySection("Staff", staff.Id))}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                    No staff details specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    ) : (
      <Box sx={{ p: 3, mt: 7, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
        {/* PDF-specific styles */}
        <style>{`
        @media print {
          #pdf-report-content {
            page-break-inside: avoid;
          }
          .page-break-before {
            page-break-before: always !important;
            break-before: always !important;
          }
          .page-break-after {
            page-break-after: always !important;
            break-after: always !important;
          }
          table {
            page-break-inside: auto !important;
          }
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
          }
          td {
            page-break-inside: avoid !important;
          }
          thead {
            display: table-header-group !important;
          }
          tfoot {
            display: table-footer-group !important;
          }
        }
        #pdf-report-content table {
          page-break-inside: auto;
        }
        #pdf-report-content tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        #pdf-report-content td {
          page-break-inside: avoid;
        }
      `}</style>
        {/* PDF Export Container */}
        {/* Download Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {/* <Button
          variant="contained"
          color="primary"
          textColor="white"
          startIcon={
            downloading ? (
              <CircularProgress size={20} color="info" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleDownloadPDF}
          sx={{ minWidth: 180 }}
          disabled={downloading}
        >
          {downloading ? "Downloading..." : "Download Report"}
        </Button> */}
        </Box>
        <div id="pdf-report-content">
          <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 1, borderRadius: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Logo and Header */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src="/main/images/v1_9old.png"
                  alt="NABL Logo"
                  style={{ height: 70, marginRight: 24 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h7"
                    sx={{ fontWeight: "bold", color: "primary.main", mb: 0.5 }}
                  >
                    National Accreditation Board for Testing and Calibration
                    Laboratories
                  </Typography>
                  <Typography
                    variant="h9"
                    sx={{ color: "text.secondary", fontWeight: "bold" }}
                  >
                    Preview Report
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Name of Laboratory:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.CabName || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Contact Person:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.NablContPerson || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Mobile:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.ContactPersonMobileNo || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Email:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {labDetail.ContactPersonEmail1 || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  UserName:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(() => {
                      console.log('Current basicDetailsState:', basicDetailsState);
                      const loginName = Array.isArray(basicDetailsState) ? basicDetailsState[0]?.LoginName : basicDetailsState?.LoginName;
                      console.log('LoginName value:', loginName);
                      return loginName || "-";
                    })()}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  CAB ID:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(() => {
                      console.log('Current basicDetailsState:', basicDetailsState);
                      const ApplicationNo = Array.isArray(basicDetailsState) ? basicDetailsState[0]?.ApplicationNo : basicDetailsState?.ApplicationNo;
                      console.log('ApplicationNo value:', ApplicationNo);
                      return ApplicationNo || "-";
                    })()}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Standard:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    ISO 15189:{(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.standard : basicDetailsState?.standard) || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Lab Type:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.LabType : basicDetailsState?.LabType) || "-"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Accreditation Category:&nbsp;
                  <span style={{ fontWeight: 400 }}>
                    {(Array.isArray(basicDetailsState) ? basicDetailsState[0]?.Assessmentype : basicDetailsState?.Assessmentype) || "-"}
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          {/* Laboratory Details Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Lab General Details
          </Typography>

          {/* Subsection: Name of the CAB */}
          <TableContainer
            component={Paper}
            sx={{ mb: 2, maxWidth: "100%", mx: "auto", borderRadius: 0 }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="right" sx={{ width: "50%" }}>
                    <strong>Details</strong>
                  </TableCell>
                  <TableCell align="left" sx={{ width: "50%" }}>
                    <strong>Data Feeded by Lab</strong>
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ width: "30%" }}>
                      <strong>Observation</strong>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Name of the CAB
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CabName || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ width: "30%", fontSize: "0.82rem" }}
                      rowSpan={17}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySection("Laboratory Details", null))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Type of CAB
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CabType === "Diagnostic"
                      ? "Medical Testing Laboratory"
                      : labDetail.CabType === "Medical Imaging"
                        ? "Medical Imaging"
                        : labDetail.CabType === "Both"
                          ? "Medical Testing Laboratory + Medical Imaging"
                          : "-"
                    }
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    GSTIN
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.GSTIN || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    GST Exemption Certificate
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.GSTExemptionFile ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.GSTExemptionFile);
                        }}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    PAN/TAN No.
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.PANTANNumber || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Address
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Address || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Country
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CountryName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    State/Province
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.StateName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    City
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.CityName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    District
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.DistrictName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Sub District
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SubDistrictName || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Village/Town
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.VillageTown || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Pin Code
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Pincode || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Mobile No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.MobileNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Landline No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.LandlineNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Fax
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Fax || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Fax ISD Code
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.FaxIsdCode || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.Email || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Are you from SEZ region (Special economic zone)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.IsFromSEZ ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.IsFromSEZ && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        SEZ Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SEZCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.SEZCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether any individual or organization has provided
                    consultancy for preparing towards NABL Accreditation
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.HasConsultancy ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.HasConsultancy && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of Individual/Organization with city
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ConsultancyName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Registered Office Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ConsultancyAddress || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Has adverse action has been initiated/taken against the laboratory in the past?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.HasAdverseAction ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.HasAdverseAction === "Yes" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of adverse action
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.AdverseActionType || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Adverse Decision Date
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.AdverseDecisionDate ? formatDate(labDetail.AdverseDecisionDate) : "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Previous Lab Id from NABL
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.PreviousLabId || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Legal Identity Details */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Legal Identity Details
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Legal Identity Type
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.LegalIdentityType || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={8}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Legal Entity Type", "legal-entity-type"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.LegalIdentityType === "Proprietorship" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of the Owner
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OwnerName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Registration Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.MCIRegNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc1 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc1);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.StateRegistrationNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc2 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc2);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Partnership" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Partnership Deed
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CompaniesActCertificate ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.CompaniesActCertificate);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Identity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "No"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of the Owner
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OwnerName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Registration Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.MCIRegNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        MCI/NMC Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc1 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc1);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate Number
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.StateRegistrationNumber || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State Registration Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentityDoc2 ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LegalIdentityDoc2);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "LLP" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Companies Act Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LLPCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.LLPCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Companies Act" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Companies Act Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CompaniesActCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.CompaniesActCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_State || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Pin Code
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CompaniesActPincode || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Public Ltd" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Public Ltd Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.PublicLtdCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.PublicLtdCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Registered Office Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "No"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Government" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Are you an Autonomous Body?
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.GovernmentIsAutonomous ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Gazette or Government Notification or self-Declaration on letter head by Head of the organization
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.GovernmentCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.GovernmentCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Trust" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Trust Certificate/Deed
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.TrustCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.TrustCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "Society" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Society Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SocietyCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.SocietyCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Are you an Autonomous Body?
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SocietyIsAutonomous ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.LegalIdentityType === "One Person Company" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Legal Entity Name
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalEntityName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Registered Office Address
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        One Person Company Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OPCCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.OPCCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Type Of Functioning */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Type Of Functioning
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Type Of Functioning
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.TypeOfFunctioning || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={8}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Type of Functioning", "type-of-functioning"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.TypeOfFunctioning === "hospital-owned" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name Of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Upload Hospital Registration Certificate
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalRegistrationCertificateFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.HospitalRegistrationCertificateFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Address of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_Address || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.LegalIdentity_State || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Pincode of Hospital
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalPincode || "No"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.TypeOfFunctioning === "hospital-outsource" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name Of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLaboratoryName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Hospital and Laboratory MOU
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HospitalLabMouFile ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.HospitalLabMouFile);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Laboratory Address of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLabAddress || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        State of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLabState || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Pincode of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceLabPincode || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider of Outsource Laboratory
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OutsourceServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.TypeOfFunctioning === "cro-lab" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.CroServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {labDetail.TypeOfFunctioning === "others" && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Type of Service Provider
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OthersServiceProvider || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Contact Person for NABL */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Contact Person for NABL
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Contact Person for NABL
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.NablContPerson || "-"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={6}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Contact Person from laboratory for NABL", "contact-person-nabl"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Designation
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SpocDesig || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Mobile No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonMobileNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Landline No
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonTelephoneNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Fax
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonFaxNo || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Email 1
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonEmail1 || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Email 2
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ContactPersonEmail2 || "-"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Category of Facility(s) Applied Under the Scope of Accreditation */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Category of Facility(s) Applied Under the Scope of Accreditation
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Permanant Facility
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.PermanentFacility ? "Yes" : "No"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={4}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Category of Facility", "category-of-facility"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Site Facility
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SiteFacility ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    POCT (Point of Care Testing)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.POCTFacility ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.CabType !== 'Medical Imaging' && (
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Category of which accreditation is being sought
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.AccreditationCategory || "-"}
                    </TableCell>
                  </TableRow>
                )}

                {/* <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Mobile Facility
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.MobileFacility ? "Yes" : "No"}
                </TableCell>
              </TableRow> */}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Parent Organisation Details */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Parent Organisation Details
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Name of the Parent Organization Applicable?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.IsParentOrgApplicable ? "Yes" : "No"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={labDetail.IsParentOrgApplicable ? 1 : 5}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Parent Organization", "parent-organization"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.IsParentOrgApplicable == true && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Name of the Parent Organization
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Contact No.
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgContactNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Landline No.
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgLandlineNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Fax No.
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgFaxNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Parent Organization Email Id
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.ParentOrgEmailId || "-"}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Collection of Sample From */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Collection of Sample From
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Sample Collection Centre(s)/ Facility(ies)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.SampleColletion ? "Yes" : "No"}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={7}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Collection of Sample From", "sample-collection"))}
                    </TableCell>
                  )}
                </TableRow>
                {labDetail.SampleColletion && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        No of Collection Centres
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.NumberOfCollectionCenters || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Relevant Document
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.SampleCollectionDocument ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.SampleCollectionDocument);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Home collection
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.HomeColletion ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.HomeColletion && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Relevant Document
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.HomeCollectionDocument ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.HomeCollectionDocument);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Other Source(s)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.OtherSource ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                {labDetail.OtherSource && (
                  <>
                    <TableRow>
                      <TableCell
                        align="right"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        Relevant Document
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "50%", fontSize: "0.82rem" }}
                      >
                        {labDetail.OtherSourcesDocument ? (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              viewS3File(labDetail.OtherSourcesDocument);
                            }}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Subsection: Internal Audit and Management Review */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Internal Audit and Management Review
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Start Date of Last Internal Audit
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.InternalAuditFromDate)}
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem" }}
                      rowSpan={7}
                      style={{ verticalAlign: "top" }}
                    >
                      {renderObservationCell(getObservationBySubSection("Internal Audit and Management Review", "internal-audit-management-review"))}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    End Date of Last Internal Audit
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.InternalAuditToDate)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Internal Audit Document
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.InternalAuditDocument ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.InternalAuditDocument);
                        }}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether all requirements of ISO 15189:2022 covering all activities of laboratory have been audited at least once in last one year?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ISO15189Audited ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether various locations (including collection centers)
                    covered in the audit?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.LocationsAudited ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Whether Pre and post examination activities included in the
                    audit schedule?
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.PrePostExamAudited ? "Yes" : "No"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Start Date of Last Management Review
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.ManagementReviewFromDate)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    End Date of Last Management Review
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {formatDate(labDetail.ManagementReviewToDate)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    Management Review Document
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ width: "50%", fontSize: "0.82rem" }}
                  >
                    {labDetail.ManagementReviewDocument ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.ManagementReviewDocument);
                        }}
                        style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {/* Subsection: Internal Audit and Management Review */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Organization Chart
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                {/* ROW 1 */}
                <TableRow>
                  <TableCell align="right" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    Organization Chart Document
                  </TableCell>

                  <TableCell align="left" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    {labDetail.OrganizationChart ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const fileurl = labDetail.OrganizationChart.split("/").pop();
                          viewS3File(fileurl);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {/* ✅ OBSERVATION CELL (RIGHT SIDE, ROWSPAN) */}
                  {isObservationVisible && (
                    <TableCell
                      align="left"
                      rowSpan={3}   // 👈 total rows in this table
                      sx={{
                        width: "20%",
                        fontSize: "0.82rem",
                        verticalAlign: "top",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {renderObservationCell(
                        getObservationBySubSection(
                          "Organization Details",
                          "organization-details"
                        )
                      )}
                    </TableCell>
                  )}
                </TableRow>

                {/* ROW 2 */}
                <TableRow>
                  <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                    Parent Organization Chart Document
                  </TableCell>
                  <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                    {labDetail.ParentOrganizationChart ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const fileurl =
                            labDetail.ParentOrganizationChart.split("/").pop();
                          viewS3File(fileurl);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>

                {/* ROW 3 */}
                <TableRow>
                  <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                    Management System Document
                  </TableCell>
                  <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                    {labDetail.ManagementSystemDocument ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const fileurl =
                            labDetail.ManagementSystemDocument.split("/").pop();
                          viewS3File(fileurl);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>


          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Proficiency Testing Documents
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    Proficiency Testing Documents
                  </TableCell>

                  <TableCell align="left" sx={{ width: "30%", fontSize: "0.82rem" }}>
                    {labDetail.QMSFile ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          viewS3File(labDetail.QMSFile);
                        }}
                      >
                        View
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>

                  {isObservationVisible && (
                    <TableCell
                      align="left"
                      rowSpan={1}
                      sx={{
                        width: "20%",
                        fontSize: "0.82rem",
                        verticalAlign: "top",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      {renderObservationCell(
                        getObservationBySubSection(
                          "Proficiency Testing Document",
                          "enclosure"
                        )
                      )}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>



          {/* Section:  Lab Discipline Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Disciplines
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 0 }}>
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": {
                  border: "1px solid #bdbdbd",
                },
                borderCollapse: "collapse",
              }}
            >
              <TableHead sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                    <strong>Sr No.</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                    <strong>Discipline Name</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disciplineState.length > 0 ? (
                  disciplineState.map((discipline, index) => (
                    <TableRow key={discipline.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {discipline.DisciplineName}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>No disciplines specified</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Lab Location Details */}
          {/* <Typography variant="h6" sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}>
            Lab Location Details 
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}>
          <Table 
          size="small"
          sx={{
    border: "1px solid #bdbdbd",
    "& th, & td": { border: "1px solid #bdbdbd" },
    borderCollapse: "collapse",
  }}>
    <TableHead>
      <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
        <TableCell align="right" sx={{ width: "50%" }}><strong>Details</strong></TableCell>
        <TableCell align="left" sx={{ width: "50%" }}><strong>Data Feeded by Lab</strong></TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {locationDetailsState.length > 0 ? locationDetailsState.map((location, idx) => (
        <>
          <TableRow key={location.Id + "-name"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Location Name</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.Name || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-group"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Address</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.Address || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-standard"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>District</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.District || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-range"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>City</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.City || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-performed"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Pin Code</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.PinCode || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-contact"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Contact No</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.ContactNo || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-fax"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Fax No.</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.FaxNo || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-email"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>E-mail</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.Email || "-"}</TableCell>
          </TableRow>
          <TableRow key={location.Id + "-laboratory-size"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Laboratory size</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{location.LaboratorySize || "-"}</TableCell>
          </TableRow>
          {idx !== locationDetailsState.length - 1 && (
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }} />
            </TableRow>
          )}
        </>
      )) : (
        <TableRow>
          <TableCell align="center" colSpan={2}>No location details specified</TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
        </TableContainer> */}

          {/* Section: Scope of Testing */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Proposed Scope
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 900
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Scope Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Range Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Equipment Details
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {scopeState.length > 0 ? (
                  [...scopeState].sort((a, b) => {
                    const disciplineA = (a.DisciplineName || '').toLowerCase();
                    const disciplineB = (b.DisciplineName || '').toLowerCase();
                    return disciplineA.localeCompare(disciplineB);
                  }).map((scope, idx) => (
                    <TableRow key={scope.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {scope.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Test Code:</strong> {scope.TestCode || "-"}</div>
                          <div><strong>Materials/Products Tested:</strong> {scope.GroupTested || "-"}</div>
                          <div><strong>Specific Test Performed:</strong> {scope.TypeOfTest || "-"}</div>
                          <div><strong>Test Method/Equipment Used:</strong> {scope.ScopeStandard || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Type:</strong> {scope.rangeType || "-"}</div>
                          <div><strong>Facility:</strong> {scope.testPerformedAt || "-"}</div>
                          <div><strong>Range:</strong> {`${scope.RangeLL || '-'} ${scope.RangeLLUnit || '-'} To ${scope.RangeUL || '-'} ${scope.RangeULUnit || '-'}`}</div>
                          <div><strong>%CV / MU:</strong> {scope.cv ? `CV: ${scope.cv}` : ''} {scope.mu ? `MU: ${scope.mu}` : ''} {!scope.cv && !scope.mu ? '-' : ''}</div>
                          {scope.rangeRemark && (
                            <div><strong>Range Remarks:</strong> {scope.rangeRemark}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Equipment:</strong> {scope.Equipment || "-"}</div>
                          <div><strong>Reference Material:</strong> {scope.RefName || "-"}</div>
                          <div><strong>Proficiency Testing:</strong> {scope.PtName || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Scope", scope.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No scope details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Proficiency Testing Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Proficiency Testing Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 900
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    PT Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    ILC Details
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Remarks
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observations
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {proficiencyTestingState.length > 0 ? (
                  proficiencyTestingState.map((pt, index) => (
                    <TableRow key={pt.Id || index}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div>
                          <div><strong>Participation In:</strong> {pt.participationIn || "-"}</div>
                          <div><strong>Nodal Laboratory:</strong> {pt.nodalLaboratory || "-"}</div>
                          <div><strong>Other Details:</strong> {pt.nodalLaboratoryOthers || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div>
                          <div><strong>ILC Details:</strong> {pt.ilcDetails || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div>
                          <div><strong>Remarks:</strong> {pt.remarks || "-"}</div>
                          <div><strong>Created:</strong> {pt.CreatedOn ? new Date(pt.CreatedOn).toLocaleDateString() : "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Proficiency Testing", `${pt.Id}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No proficiency testing details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>


          {/* Section: Organization Staff Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Organization Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 1000
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Personal Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Contact Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Work Information
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {organisationDetailsState.length > 0 ? (
                  organisationDetailsState.map((staff, idx) => (
                    <TableRow key={staff.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {staff.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Name:</strong> {staff.Name || "-"}</div>
                          <div><strong>Designation:</strong> {staff.Designation || "-"}</div>
                          <div><strong>Qualification:</strong> {staff.Qualification || "-"}</div>
                          <div><strong>Specialization:</strong> {staff.Specialization || "-"}</div>
                          <div><strong>Experience:</strong> {staff.RelevantExperience || "-"} years</div>
                          <div><strong>Training:</strong> {staff.RelevantTraining || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Landline:</strong> {staff.LandlineNo || "-"}</div>
                          <div><strong>Mobile:</strong> {staff.ContactNo || "-"}</div>
                          <div><strong>Email:</strong> {staff.EmailID || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Employee Type:</strong> {staff.EmployeeType || "-"}</div>
                          <div><strong>Engagement Type:</strong> {staff.typeofEngagement || "-"}</div>
                          <div><strong>Timing:</strong> {staff.Timming || "-"}</div>
                          <div><strong>Authorized Signatory:</strong> {staff.IsAuthorisedSignatory ? "Yes" : "No"}</div>
                          {staff.IsAuthorisedSignatory && staff.SignatorySign && (
                            <div><strong>Signatory File:</strong> <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                viewS3File(staff.SignatorySign);
                              }}
                              style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                            >
                              View
                            </a></div>
                          )}
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Staff", staff.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No staff details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Equipment Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Equipment Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 1100
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Equipment Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Technical Specifications
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 240, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Calibration Information
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {equipmentDetailsState.length > 0 ? (
                  equipmentDetailsState.map((equipment, idx) => (
                    <TableRow key={equipment.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {equipment.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Equipment Name:</strong> {equipment.NameOfEquipment || "-"}</div>
                          <div><strong>Equipment ID:</strong> {equipment.Equipmentid || "-"}</div>
                          <div><strong>UID:</strong> {equipment.Uid || "-"}</div>
                          <div><strong>Model:</strong> {equipment.Model || "-"}</div>
                          <div><strong>Serial No:</strong> {equipment.SerialNo || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Manufacturer:</strong> {equipment.MakeCompany || "-"}</div>
                          <div><strong>Year of Make:</strong> {equipment.YearOfMake || "-"}</div>
                          <div><strong>Receipt Date:</strong> {formatDate(equipment.ReceiptDate) || "-"}</div>
                          <div><strong>Installation Date:</strong> {formatDate(equipment.DateOfInstallation) || "-"}</div>
                          <div><strong>Limit of Detection:</strong> {equipment.LimitOfDetection || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Calibration Date:</strong> {formatDate(equipment.DateOfLastCalibration) || "-"}</div>
                          <div><strong>Calibration Due:</strong> {formatDate(equipment.CalibrationDueOn) || "-"}</div>
                          <div><strong>Calibrated By:</strong> {equipment.CalibratedByType || "-"}</div>
                          <div><strong>Calibration Agency:</strong> {equipment.CalibratedByType === "In-house" ? equipment.InHouseCalibration || "-" : equipment.ExternalCalibrationAgency || "-"}</div>
                          <div><strong>Traceability:</strong> {equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Equipment", `${equipment.Id}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 5 : 4}>
                      No equipment details specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Reference Materials */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Reference Material Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 900
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Discipline
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 300, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Material Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 250, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Source & Traceability
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {referenceMaterialsState.length > 0 ? (
                  referenceMaterialsState.map((material, idx) => (
                    <TableRow key={material.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {material.DisciplineName || "-"}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Name:</strong> {material.Name || "-"}</div>
                          <div><strong>Type:</strong> Reference Material/Strain/Culture</div>
                          <div><strong>Date of Expiry:</strong> {formatDate(material.DateOfExpiry) === "01/01/1900" ? "-" : formatDate(material.DateOfExpiry)}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Source:</strong> {material.Source || "-"}</div>
                          <div><strong>Traceability:</strong> {material.Traceability || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Reference Material", material.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 4 : 3}>
                      No reference materials specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Non Analytical Instruments Details */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
          >
            Non Analytical Instruments Details
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto", overflowX: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
                minWidth: 800
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 250, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Instrument Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 300, fontSize: "0.82rem", fontWeight: "bold" }}>
                    Calibration Information
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 150, fontSize: "0.82rem", fontWeight: "bold" }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {nonAnalyticalInstrumentsState.length > 0 ? (
                  nonAnalyticalInstrumentsState.map((instrument, idx) => (
                    <TableRow key={instrument.Id}>
                      <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Instrument Name:</strong> {instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : "-"}</div>
                          <div><strong>UID:</strong> {instrument.UID || "-"}</div>
                        </div>
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div><strong>Calibration Frequency:</strong> {instrument.Calibration_Frequency || "-"}</div>
                          <div><strong>Last Calibration Date:</strong> {formatDate(instrument.LastCalibration_Date) || "-"}</div>
                          <div><strong>Next Calibration Date:</strong> {formatDate(instrument.NextCalibration_Date) || "-"}</div>
                        </div>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ fontSize: "0.82rem", verticalAlign: "top" }}>
                          {renderObservationCell(getObservationBySection("Non-Analytical Instruments", `${instrument.id}`))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={isObservationVisible ? 3 : 2}>
                      No Non Analytical Instruments Specified
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section: Phantoms Details */}
          {(labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
              >
                Phantoms Details
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
              >
                <Table
                  size="small"
                  sx={{
                    border: "1px solid #bdbdbd",
                    "& th, & td": { border: "1px solid #bdbdbd" },
                    borderCollapse: "collapse",
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                      <TableCell align="right" sx={{ width: "50%" }}>
                        <strong>Details</strong>
                      </TableCell>
                      <TableCell align="left" sx={{ width: "50%" }}>
                        <strong>Data Feeded by Lab</strong>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ width: "30%" }}>
                          <strong>Observation</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form153PhantomsState.length > 0 ? (
                      form153PhantomsState.map((phantom, idx) => (
                        <>
                          <TableRow key={phantom.Id + "-PhantomName"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Phantom Type
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.PhantomType || "-"}
                            </TableCell>
                            {isObservationVisible && (
                              <TableCell
                                align="center"
                                sx={{ fontSize: "0.82rem" }}
                                rowSpan={nonAnalyticalInstrumentsState.length}
                                style={{ verticalAlign: "top" }}
                              >
                                {renderObservationCell(getObservationBySection("phantoms", phantom.Id))}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow key={phantom.Id + "-Discipline"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Discipline
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.DisciplineName || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-typeoftest"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Type of Test
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.TypeOfTestNames || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-Specifications"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Specifications
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.Specifications || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-DateofPlacing"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Date of Placing
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {formatDate(phantom.DateofPlacing) || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={phantom.Id + "-Traceability"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Traceability
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {phantom.Traceability || "-"}
                            </TableCell>
                          </TableRow>
                          {idx !== form153PhantomsState.length - 1 && (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                              />
                            </TableRow>
                          )}
                        </>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={2}>
                          No Phantoms Specified
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {/* Section: Radioisotopes/Test Kits Details */}
          {(labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && (
            <>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
              >
                Radioisotopes/Test Kits Details
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
              >
                <Table
                  size="small"
                  sx={{
                    border: "1px solid #bdbdbd",
                    "& th, & td": { border: "1px solid #bdbdbd" },
                    borderCollapse: "collapse",
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                      <TableCell align="right" sx={{ width: "50%" }}>
                        <strong>Details</strong>
                      </TableCell>
                      <TableCell align="left" sx={{ width: "50%" }}>
                        <strong>Data Feeded by Lab</strong>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center" sx={{ width: "30%" }}>
                          <strong>Observation</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {radioDetailsState.length > 0 ? (
                      radioDetailsState.map((radio, idx) => (
                        <>
                          <TableRow key={radio.Id + "-RadioName"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Name of Radioisotopes
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.NameofRadioisotopes || "-"}
                            </TableCell>
                            {isObservationVisible && (
                              <TableCell
                                align="center"
                                sx={{ fontSize: "0.82rem" }}
                                rowSpan={nonAnalyticalInstrumentsState.length}
                                style={{ verticalAlign: "top" }}
                              >
                                {renderObservationCell(getObservationBySection("radioisotopes", radio.Id))}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow key={radio.Id + "-Discipline"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Discipline
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.DisciplineName || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-typeoftest"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Type of Test
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.TypeOfTestNames || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-HalfLife"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Half Life
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.HalfLife || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-Energy"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Energy (KeV)
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.EnergyKeV || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-MaxActivityPermitted"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Max Activity Permitted
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.MaxactivityPermitted || "-"}
                            </TableCell>
                          </TableRow>
                          <TableRow key={radio.Id + "-DetailsOfSupplier"}>
                            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                              Details of Supplier
                            </TableCell>
                            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                              {radio.DetailsofSupplier || "-"}
                            </TableCell>
                          </TableRow>
                          {idx !== radioDetailsState.length - 1 && (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                              />
                            </TableRow>
                          )}
                        </>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={2}>
                          No Radioisotopes/Test Kits Specified
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Declaration Section */}
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 2, mb: 2, mt: 4, textAlign: "center" }}
          >
            DECLARATION
          </Typography>

          {/* Terms & Conditions Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Terms & Conditions
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
          >
            The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
          >
            The Conformity Assessment Body {"{"}Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP){"}"} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
          >
            By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, lineHeight: 1.6, textAlign: "justify" }}
          >
            Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.
          </Typography>

          {/* Declaration by the Laboratory Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Declaration by the Laboratory
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            We declare that:
          </Typography>

          <Box component="ol" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We have declared all sources of samples.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We have declared and submitted accurate and complete information in the enclosed "Declaration Form"
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              We comply to all national, regional and local regulatory requirements for operating a laboratory.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              All information provided in this application is true.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}>
              Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO 15189.
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}>
              {labDetail.DeclNameOrg || 'The authorized representative of CAB'}, authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}>
              I have read and understood all the terms and conditions mentioned above. *
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}>
              By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'. *
            </Typography>
          </Box>
        </div>
      </Box>
    )

  );
};

export default MedicalPreviewReport;
